// @flow

import { map as _map } from 'lodash';
import { pipe, mapValues, countBy } from 'lodash/fp';

import { type DiagnoseResult, type Order, mergeDetailResult } from './types';
import { GenderBasedOrder, OrderGenerator, ToppingOrder } from './order';
import { type Pizza, Toppings } from './pizza';
import { Random } from '../../../../utils/Random';
import { customerDisfavors, hungrinessFitTable, hungrinessString } from './constants';

export type CustomerType = $Keys<typeof customerDisfavors>;
export type Hungriness = $Keys<typeof hungrinessString>;

import { genRuby } from '@sonyged/ext-proc';

const hungryTalk = (hungriness: Hungriness): string => {
  return hungrinessString[hungriness];
};

class Customer {
  type: CustomerType;
  hungriness: Hungriness;
  order: Order;
  subtype: number;

  constructor(type: CustomerType, subtype: number, hungriness: Hungriness, order: Order) {
    this.type = type;
    this.subtype = subtype;
    this.hungriness = hungriness;
    this.order = order;
  }

  get disfavors() {
    return customerDisfavors[this.type];
  }

  genderFitScore(pizza: Pizza): number {
    if (customerDisfavors[this.type].some(n => pizza.hasTopping(n))) {
      return -2;
    }
    return 0;
  }

  hungrinessScore(pizza: Pizza): number {
    return hungrinessFitTable[this.hungriness][pizza.crust];
  }

  satisfactionScore(pizza: Pizza): number {
    const score =
      this.order.fitScore(pizza) + this.genderFitScore(pizza) + this.hungrinessScore(pizza) + pizza.tasteFitScore;
    if (score > 5) return 5;
    if (score < -5) return -5;
    return score;
  }

  /*
   * Toppings depends on order
   * Sauce depends on tastefit
   * Crust depends on hungriness
   */
  diagnose(pizza: Pizza): DiagnoseResult {
    // crust
    const fromHungriness = [[pizza.crust, this.hungrinessScore(pizza)]];

    // sauce
    const fromTaste = pizza.tasteFitScoreDetail();

    // topping
    const fromOrder = this.order.fitScoreDetail(pizza);

    const dislikedToppings = pizza.toppings.filter(topping =>
      customerDisfavors[this.type].some(name => name === topping.topping)
    );
    const avgScore = dislikedToppings.length ? this.genderFitScore(pizza) / dislikedToppings.length : 0;
    const toppingScores = pipe(
      countBy('topping'),
      mapValues(count => count * avgScore)
    )(dislikedToppings);
    const fromGender = _map(toppingScores, (count: number, toppingName: string) => [Toppings[toppingName], count]);

    // result
    const result = [fromHungriness, fromTaste, fromOrder, fromGender].reduce(mergeDetailResult);

    const like = result.filter(([_, score]) => score > 0).map(([ingredient, _]) => ingredient);
    const dislike = result.filter(([_, score]) => score < 0).map(([ingredient, _]) => ingredient);

    return { like, dislike };
  }

  talk() {
    return genRuby(`${hungryTalk(this.hungriness)} ${this.order.toString()}`);
  }
}

const customerOrderConstraint = (customer: Customer) => {
  const order = customer.order;
  if (order instanceof ToppingOrder && order.type.kind === 'like') {
    const hasDisfavorTopping = order.type.toppings.some(topping =>
      customerDisfavors[customer.type].some(toppingName => toppingName === topping.topping)
    );
    return !hasDisfavorTopping;
  }
  return true;
};

const randomHungriness: Random<Hungriness> = Random.of('very_hungry', 'somewhat_hungry', 'normal');
const randomCustomerType: Random<CustomerType> = Random.of('man', 'woman', 'child');
const CustomerGenerator: Random<Customer> = OrderGenerator.bind(order =>
  randomHungriness.bind(hungriness =>
    randomCustomerType.bind(customerType =>
      Random.int(1, 3).map(subtype => new Customer(customerType, subtype, hungriness, order))
    )
  )
).filter(customerOrderConstraint);

const customerGroupOrderConstraint = (customers: Customer[]) => {
  const orders = customers.map(c => c.order);
  const customerTypes = customers.map(c => c.type);

  const genderBasedOrderTargetOnlyGenderInGroup = orders.every(order => {
    if (order instanceof GenderBasedOrder) {
      if (order.type === 'popular') return true;
      if (order.type === 'child') return customerTypes.some(ct => ct === 'child');
      if (order.type === 'adult') return customerTypes.some(ct => ct !== 'child');
    }
    return true;
  });

  const orderCounts = countBy(o => o.toString(), orders);
  const noDuplicateOrder = Object.values(orderCounts).every(x => x === 1);

  const avatarCount = countBy(c => c.type + c.subtype, customers);
  const noDuplicateAvatar = Object.values(avatarCount).every(x => x === 1);

  return genderBasedOrderTargetOnlyGenderInGroup && noDuplicateOrder && noDuplicateAvatar;
};

const CustomerGroupGenerator = (memberCount: number): Random<Customer[]> => {
  return Random.arrayOfLength(CustomerGenerator, memberCount).filter(customerGroupOrderConstraint);
};

export { Customer, CustomerGenerator, CustomerGroupGenerator };
